import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { workspaceApi } from '../api/workspace';
import { EmptyState, Loading, PageError } from '../components/State';
import { ageFromDob, money } from '../lib/format';
import { EventRow } from '../components/EventRow';
import './YouthProfilePage.css';

type Tab = 'overview' | 'medications' | 'funding' | 'schedule';

export function YouthProfilePage() {
  const { connectionId = '' } = useParams();
  const [tab, setTab] = useState<Tab>('overview');

  const clients = useQuery({ queryKey: ['clients'], queryFn: workspaceApi.clients });
  const client = clients.data?.find((c) => c.connectionId === connectionId);
  const name = client?.youthSnapshot?.name ?? 'Youth';
  const age = ageFromDob(client?.youthSnapshot?.dob);

  const services = useQuery({
    queryKey: ['services', connectionId],
    queryFn: () => workspaceApi.services(connectionId),
    enabled: !!connectionId,
  });
  const meds = useQuery({
    queryKey: ['medications', connectionId],
    queryFn: () => workspaceApi.medications(connectionId),
    enabled: !!connectionId && tab === 'medications',
  });
  const funding = useQuery({
    queryKey: ['funding', connectionId],
    queryFn: () => workspaceApi.funding(connectionId),
    enabled: !!connectionId && tab === 'funding',
  });
  const events = useQuery({
    queryKey: ['events', connectionId],
    queryFn: () => workspaceApi.events(connectionId),
    enabled: !!connectionId && (tab === 'schedule' || tab === 'overview'),
  });

  if (clients.isLoading) return <Loading />;

  return (
    <div>
      <Link to="/families" className="yp__back">
        ‹ All families
      </Link>
      <div className="yp__head">
        <h1>{name}</h1>
        <span className="muted">{age != null ? `Age ${age}` : 'Age —'}</span>
      </div>

      <div className="tabbar">
        {(['overview', 'medications', 'funding', 'schedule'] as Tab[]).map((t) => (
          <button
            key={t}
            className={`tabbtn${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="yp__stack">
          <section>
            <h2>Services you provide</h2>
            {services.isLoading ? (
              <Loading />
            ) : services.isError ? (
              <PageError error={services.error} onRetry={() => services.refetch()} />
            ) : (services.data ?? []).length === 0 ? (
              <EmptyState text="No services recorded for this youth yet." />
            ) : (
              <div className="yp__chips">
                {services.data!.map((s) => (
                  <span key={s} className="yp__chip">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2>Next shifts</h2>
            {events.isLoading ? (
              <Loading />
            ) : events.isError ? (
              <PageError error={events.error} onRetry={() => events.refetch()} />
            ) : (
              <UpcomingEvents connectionId={connectionId} youthName={name} events={events.data ?? []} />
            )}
          </section>
        </div>
      )}

      {tab === 'medications' && (
        <section>
          <h2>Medications</h2>
          <p className="muted yp__note">Read-only — maintained by the caregiver.</p>
          {meds.isLoading ? (
            <Loading />
          ) : meds.isError ? (
            <PageError error={meds.error} onRetry={() => meds.refetch()} />
          ) : (meds.data ?? []).length === 0 ? (
            <EmptyState text="No active medications shared for this youth." />
          ) : (
            <div className="card yp__list">
              {meds.data!.map((m) => (
                <div key={m.id} className="yp__row">
                  <div>
                    <div className="yp__row-title">{m.name}</div>
                    <div className="muted yp__row-sub">
                      {[m.dosage, m.frequency].filter(Boolean).join(' · ')}
                      {m.times.length ? ` · ${m.times.join(', ')}` : ''}
                    </div>
                    {m.notes && <div className="muted yp__row-sub">{m.notes}</div>}
                  </div>
                  <div className="muted yp__row-sub">{m.administeredBy}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'funding' && (
        <section>
          <h2>Funding</h2>
          {funding.isLoading ? (
            <Loading />
          ) : funding.isError ? (
            <PageError error={funding.error} onRetry={() => funding.refetch()} />
          ) : (funding.data ?? []).length === 0 ? (
            <EmptyState text="No funding records for this youth." />
          ) : (
            <div className="card yp__list">
              {funding.data!.map((f) => (
                <div key={f.id} className="yp__row">
                  <div>
                    <div className="yp__row-title">{f.funder}</div>
                    <div className="muted yp__row-sub">
                      {[f.serviceType, f.period.replace('_', ' ')].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="yp__row-sub">{money(f.amount, f.currency)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'schedule' && (
        <section>
          <h2>Schedule</h2>
          {events.isLoading ? (
            <Loading />
          ) : events.isError ? (
            <PageError error={events.error} onRetry={() => events.refetch()} />
          ) : (events.data ?? []).length === 0 ? (
            <EmptyState text="No events scheduled for this youth." />
          ) : (
            <div className="yp__events">
              {[...events.data!]
                .sort((a, b) => b.date.localeCompare(a.date))
                .map((e) => (
                  <EventRow key={e.id} event={e} youthName={name} />
                ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function UpcomingEvents({
  events,
  youthName,
}: {
  connectionId: string;
  youthName: string;
  events: import('../api/workspace').CareEvent[];
}) {
  const today = new Date().toISOString().split('T')[0];
  const upcoming = events
    .filter((e) => e.date >= today && e.status !== 'cancelled')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);
  if (upcoming.length === 0) return <EmptyState text="No upcoming shifts." />;
  return (
    <div className="yp__events">
      {upcoming.map((e) => (
        <EventRow key={e.id} event={e} youthName={youthName} />
      ))}
    </div>
  );
}
